import NotFound from '@/components/common/NotFound'
import { Box, Button, Dialog, Drawer } from '@mui/material'
import { Route, Routes, useLocation } from 'react-router-dom'
import ControllerModal from '../config/modal/ControllerModal'
import CommonLoading from './components/common/CommonLoading'
import ConfigurationModal from './components/modal/ConfigurationModal'
import Mk2DecoderModal from './components/modal/Mk2DecoderModal'
import Mk2DecoderModalV2 from './components/modal/Mk2DecoderModalV2'
import ReviewDecodersView from './components/modal/ReviewDecodersView'
import ConfigSetUp from './components/pages/ConfigSetUp'
import { ET } from './components/pages/ET'
import ETData from './components/pages/ET/ETData'
import { Emitter, EmitterData } from './components/pages/Emitter'
import { Historical, HistoricalData } from './components/pages/Historical'
import { PipesConfig, PipesData } from './components/pages/PipesConfig'
import { Plant, PlantData } from './components/pages/Plant'
import { Pump, PumpData } from './components/pages/Pump'
import { Sensor, SensorData } from './components/pages/Sensor'
import { SetUpFinish } from './components/pages/SetUpFinish'
import { DiscoveryData, SetUpFirst } from './components/pages/SetUpFirst'
import { SetUpFour } from './components/pages/SetUpFour'
import {
  // SetUpSecond, // V1: Comment out to use V2
  SetupEmitter,
  SetupEtZone,
  SetupPlant,
} from './components/pages/SetUpSecond'
// V2: Active
import { SetUpSecondV2 as SetUpSecond } from './components/pages/SetUpSecondV2'
// SCRUM-1032: guided (in-app) alternative to the Excel Common Data import.
import SetUpGuided from './components/pages/SetUpGuided/SetUpGuided'
import SetupAreas from './components/pages/SetUpSecond/SetupAreas'
import SetupReference from './components/pages/SetUpSecond/SetupReference'
import SetupSoil from './components/pages/SetUpSecond/SetupSoil'
import { SetUpThird } from './components/pages/SetUpThird'
import { Sites, SitesData } from './components/pages/Sites'
import { Soil, SoilData } from './components/pages/Soil'
import { Station, StationData } from './components/pages/Station'
import { StationAssignment } from './components/pages/StationAssignment'
import StationAssignmentData from './components/pages/StationAssignment/StationAssignmentData'
import SetUpView from './components/sidebar/SetUpView'
import {
  MODAL_VIEW,
  SIDEBAR_VIEW,
  SetUpUIProvider,
  useSetUpUI,
} from './context'
import { CONFIG_SETUP_ROUTES } from './routes'
import { OPERATIONS_ROUTES } from '@/constant/route'
import useValidateCurrentInterface from '@features/design-system/hooks/setup/useValidateCurrentInterface'
import useSetupUrlInterfaceId from '@features/design-system/hooks/setup/useSetupUrlInterfaceId'
import useSearchInterface from '@features/admin/hooks/interface/useSearchInterface'
import useTranslation from '@/hooks/useTranslation'

function SetUpContainer() {
  const { t } = useTranslation()

  const location = useLocation()
  const { data: allInterfaces, isError, refetch } = useSearchInterface()

  // SCRUM-976: this component renders the OUTER `config-setup/*` route, so useParams()
  // here can't see the :id captured by the nested setup_first/:id, discovery/:id, etc.
  // routes rendered below - react-router only flows matched params down, not back up to
  // the ancestor that renders <Routes>.
  const idFromPath = useSetupUrlInterfaceId()

  // Disable autoRedirect for setup routes that handle their own navigation.
  // Without this, React Query's fresh status (true) triggers redirect to dashboard
  // while Redux still has stale status (false), causing an infinite redirect loop.
  // See SCRUM-880, SCRUM-895.
  const isSetupRoute =
    location.pathname.includes('setup_second') ||
    location.pathname.includes('setup_first') ||
    location.pathname.includes('discovery')

  const { isLoading } = useValidateCurrentInterface({
    currentInterfaceId: idFromPath,
    allInterfaces,
    redirectTo: OPERATIONS_ROUTES.index.to,
    autoRedirect: !isSetupRoute,
  })

  // Show loading while fetching interface data
  if (isLoading && !isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Box sx={{ textAlign: 'center' }}>Loading...</Box>
      </Box>
    )
  }

  // SCRUM-976: previously this state was unreachable - isLoading stayed true forever
  // when the interface list request failed, trapping the user on the loading screen.
  if (isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: '16px',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>Unable to load interface data.</Box>
        <Button variant="primary" onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <SetUpUIProvider>
      <Routes>
        {/* first */}
        <Route index element={<ConfigSetUp />} />
        <Route
          path={CONFIG_SETUP_ROUTES.index.path}
          element={<ConfigSetUp />}
        />
        <Route
          path={CONFIG_SETUP_ROUTES.setupFirstUser.path}
          element={<SetUpFirst />}
        />
        <Route
          path={CONFIG_SETUP_ROUTES.setupFirst.path}
          element={<SetUpFirst />}
        />
        <Route
          path={CONFIG_SETUP_ROUTES.discovery.path}
          element={<DiscoveryData />}
        />

        {/* setupSecond */}
        <Route
          path={CONFIG_SETUP_ROUTES.setupSecond.path}
          element={<SetUpSecond />}
        />
        {/* SCRUM-1032: guided in-app alternative to the Excel import */}
        <Route
          path={CONFIG_SETUP_ROUTES.setupSecondGuided.path}
          element={<SetUpGuided />}
        />
        <Route
          path={CONFIG_SETUP_ROUTES.setupPlant.path}
          element={<SetupPlant />}
        />
        <Route
          path={CONFIG_SETUP_ROUTES.setupSoil.path}
          element={<SetupSoil />}
        />
        <Route
          path={CONFIG_SETUP_ROUTES.setupEmitter.path}
          element={<SetupEmitter />}
        />
        <Route
          path={CONFIG_SETUP_ROUTES.setupEtReference.path}
          element={<SetupReference />}
        />
        <Route
          path={CONFIG_SETUP_ROUTES.setupEtZone.path}
          element={<SetupEtZone />}
        />
        <Route
          path={CONFIG_SETUP_ROUTES.setupAreas.path}
          element={<SetupAreas />}
        />

        <Route path={CONFIG_SETUP_ROUTES.sites.path} element={<Sites />} />

        <Route
          path={CONFIG_SETUP_ROUTES.sitesCommon.path}
          element={
            <CommonLoading
              titleHeader="Sites"
              title="Data 1 of 9"
              subtitle="Sites and Locations"
              link={CONFIG_SETUP_ROUTES.dataSites.to}
            />
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.dataSites.path}
          element={<SitesData />}
        />

        <Route
          path={CONFIG_SETUP_ROUTES.pipes.path}
          element={<PipesConfig />}
        />

        <Route
          path={CONFIG_SETUP_ROUTES.pipesCommon.path}
          element={
            <CommonLoading
              titleHeader="Pipes"
              title="Data 2 of 9"
              subtitle="Pipes"
              link={CONFIG_SETUP_ROUTES.dataPipes.to}
            />
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.dataPipes.path}
          element={<PipesData />}
        />

        <Route path={CONFIG_SETUP_ROUTES.pump.path} element={<Pump />} />

        <Route
          path={CONFIG_SETUP_ROUTES.pumpCommon.path}
          element={
            <CommonLoading
              titleHeader="Pump"
              title="Data 3 of 9"
              subtitle="Pump"
              link={CONFIG_SETUP_ROUTES.dataPump.to}
            />
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.dataPump.path}
          element={<PumpData />}
        />

        <Route path={CONFIG_SETUP_ROUTES.plant.path} element={<Plant />} />

        <Route
          path={CONFIG_SETUP_ROUTES.plantCommon.path}
          element={
            <CommonLoading
              titleHeader="Plant"
              title="Data 4 of 9"
              subtitle="Plant"
              link={CONFIG_SETUP_ROUTES.dataPlant.to}
            />
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.dataPlant.path}
          element={<PlantData />}
        />

        <Route path={CONFIG_SETUP_ROUTES.soil.path} element={<Soil />} />

        <Route
          path={CONFIG_SETUP_ROUTES.soilCommon.path}
          element={
            <CommonLoading
              titleHeader="Soil' title="
              title="Data 5 of 9"
              subtitle="Soil"
              link={CONFIG_SETUP_ROUTES.dataSoil.to}
            />
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.dataSoil.path}
          element={<SoilData />}
        />

        <Route
          path={CONFIG_SETUP_ROUTES.historical.path}
          element={<Historical />}
        />

        <Route
          path={CONFIG_SETUP_ROUTES.historicalCommon.path}
          element={
            <CommonLoading
              titleHeader="Historical"
              title="Data 6  of 9"
              subtitle="Historical ET"
              link={CONFIG_SETUP_ROUTES.dataHistorical.to}
            />
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.dataHistorical.path}
          element={<HistoricalData />}
        />

        <Route path={CONFIG_SETUP_ROUTES.emitter.path} element={<Emitter />} />

        <Route
          path={CONFIG_SETUP_ROUTES.emitterCommon.path}
          element={
            <CommonLoading
              titleHeader="Emitter"
              title="Data 7 of 9"
              subtitle="Emitter"
              link={CONFIG_SETUP_ROUTES.dataEmitter.to}
            />
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.dataEmitter.path}
          element={<EmitterData />}
        />

        <Route path={CONFIG_SETUP_ROUTES.sensor.path} element={<Sensor />} />

        <Route
          path={CONFIG_SETUP_ROUTES.sensorCommon.path}
          element={
            <CommonLoading
              titleHeader="Sensor"
              title="Data 8 of 9"
              subtitle="Sensor"
              link={CONFIG_SETUP_ROUTES.dataSensor.to}
            />
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.dataSensor.path}
          element={<SensorData />}
        />

        <Route path={CONFIG_SETUP_ROUTES.et.path} element={<ET />} />

        <Route
          path={CONFIG_SETUP_ROUTES.etCommon.path}
          element={
            <CommonLoading
              titleHeader="ET"
              title="Data 9 of 9"
              subtitle={ t('formsLabelsMicroClimate') }
              link={CONFIG_SETUP_ROUTES.dataET.to}
            />
          }
        />

        <Route path={CONFIG_SETUP_ROUTES.dataET.path} element={<ETData />} />

        <Route
          path={CONFIG_SETUP_ROUTES.setupThird.path}
          element={<SetUpThird />}
        />

        <Route path={CONFIG_SETUP_ROUTES.station.path} element={<Station />} />
        <Route
          path={CONFIG_SETUP_ROUTES.stationCommon.path}
          element={
            <CommonLoading
              titleHeader="Station"
              title="Station Data"
              subtitle=""
              link={CONFIG_SETUP_ROUTES.dataStation.to}
            />
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.dataStation.path}
          element={<StationData />}
        />

        <Route
          path={CONFIG_SETUP_ROUTES.setupFour.path}
          element={<SetUpFour />}
        />
        <Route
          path={CONFIG_SETUP_ROUTES.stationAssignment.path}
          element={<StationAssignment />}
        />

        <Route
          path={CONFIG_SETUP_ROUTES.stationAssignmentCommon.path}
          element={
            <CommonLoading
              titleHeader="Station Assignment"
              title="Station Assignment"
              subtitle=""
              link={CONFIG_SETUP_ROUTES.dataStationAssignment.to}
            />
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.dataStationAssignment.path}
          element={<StationAssignmentData />}
        />

        <Route
          path={CONFIG_SETUP_ROUTES.setupFinish.path}
          element={<SetUpFinish />}
        />

        <Route path="*" element={<NotFound />} />
      </Routes>

      <SidebarUI />
      <ModalUI />
    </SetUpUIProvider>
  )
}

const SidebarView: React.FC<{
  sidebarView: SIDEBAR_VIEW
  closeSidebar(): any
}> = ({ sidebarView, closeSidebar }) => {
  return (
    <Drawer
      variant="temporary"
      ModalProps={{
        keepMounted: true,
      }}
      open={Boolean(sidebarView.type)}
      anchor="right"
      onClose={closeSidebar}
    >
      <Box bgcolor="white" color="#000" flexGrow={1}>
        {sidebarView.type === 'SETUP_VIEW' && <SetUpView />}
      </Box>
    </Drawer>
  )
}

const SidebarUI: React.FC = () => {
  const { displaySidebar, closeSidebar, sidebarView } = useSetUpUI()
  return displaySidebar ? (
    <SidebarView sidebarView={sidebarView} closeSidebar={closeSidebar} />
  ) : null
}

const ModalView: React.FC<{
  modalView: MODAL_VIEW
  displayModal: boolean
  closeModal(): any
}> = ({ modalView, displayModal, closeModal }) => {
  return (
    <Dialog open={displayModal} onClose={closeModal}>
      {modalView.type === 'REVIEW_DECODERS_VIEW' && <ReviewDecodersView />}
      {modalView.type === 'CONFIGURATION_MODAL' && <ConfigurationModal />}
      {modalView.type === 'MK2_DECODER_MODAL' && <Mk2DecoderModal />}
      {modalView.type === 'MK2_DECODER_MODAL_V2' && <Mk2DecoderModalV2 />}
      {modalView.type === 'CONTROLLER_MODAL' && (
        <ControllerModal
          text={modalView.data?.text}
          onClose={modalView.data?.onClose}
        />
      )}
    </Dialog>
  )
}

const ModalUI: React.FC = () => {
  const { displayModal, closeModal, modalView } = useSetUpUI()

  return displayModal ? (
    <ModalView
      modalView={modalView}
      closeModal={closeModal}
      displayModal={displayModal}
    />
  ) : null
}

export default SetUpContainer
